import {
  Box,
  Grid,
  Paper,
  Typography,
  Stack,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ApartmentIcon from "@mui/icons-material/Apartment";
import DescriptionIcon from "@mui/icons-material/Description";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import GroupsIcon from "@mui/icons-material/Groups";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { useNavigate } from "react-router-dom";

import { useAppSelector } from "../../../redux/hook";
import { getUserDisplayName } from "../../../utils/userDisplay";
import { useListningQuery } from "../../../redux/api/authApi";
import SarIcon from "../../icons/SarIcon";
import StatCard from "../StatCard";

function formatCompactMoney(num, symbol = "SAR ") {
  if (num == null || Number.isNaN(Number(num))) return "—";
  const n = Number(num);
  if (Math.abs(n) >= 1e6) return `${symbol}${(n / 1e6).toFixed(2)}M`;
  if (Math.abs(n) >= 1e3) return `${symbol}${(n / 1e3).toFixed(1)}K`;
  return `${symbol}${n.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

const QuickActionItem = ({ icon, title, subtitle, onClick }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={
      onClick
        ? (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onClick(e);
            }
          }
        : undefined
    }
    sx={{
      px: 3,
      py: 2.5,
      borderRadius: 2,
      border: (theme) => `1px solid ${theme.palette.border.light}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      mb: 1.5,
      bgcolor: "background.paper",
      ...(onClick
        ? {
            cursor: "pointer",
            transition: "background-color 0.15s ease",
            "&:hover": { bgcolor: "action.hover" },
          }
        : {}),
    }}
  >
    <Stack direction="row" spacing={2} alignItems="center">
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "50%",
          bgcolor: (theme) => theme.palette.brand.cyanSoft,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.muted" }}>
          {subtitle}
        </Typography>
      </Box>
    </Stack>
    <ArrowForwardIosIcon sx={{ fontSize: 16, color: "text.arrow" }} />
  </Paper>
);

const AssetOwnerDashboard = () => {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth?.user);
  const displayName = getUserDisplayName(user);
  const { data, isLoading, error, refetch } = useListningQuery();

  const handleListNewAsset = () => {
    navigate("/asset/list-new-asset");
  };

  const handleManageListings = () => {
    navigate("/asset/listings");
  };

  const summary = data?.summary || {};

  const statCards = [
    {
      key: "total-raised",
      icon: (
        <SarIcon
          aria-hidden
          sx={{ color: (theme) => theme.palette.stats.green, fontSize: 22 }}
        />
      ),
      title: "Total Raised",
      value: isLoading ? "—" : formatCompactMoney(summary.totalRaisedFund),
      // subtitle: isLoading ? undefined : raisedSubtitle,
      // subtitleColor: (theme) => theme.palette.stats.green,
    },
    {
      key: "total-investors",
      icon: (
        <GroupsIcon
          sx={{ color: (theme) => theme.palette.stats.blue, fontSize: 22 }}
        />
      ),
      title: "Total Investors",
      value:
        isLoading
          ? "—"
          : summary.totalInvestors != null
            ? String(summary.totalInvestors)
            : "—",
      // subtitle: isLoading
      //   ? undefined
      //   : summary.newInvestorsThisWeek != null
      //     ? `+${summary.newInvestorsThisWeek} this week`
      //     : undefined,
      // subtitleColor: (theme) => theme.palette.stats.blue,
    },
    {
      key: "active-listings",
      icon: (
        <ApartmentIcon
          sx={{ color: (theme) => theme.palette.stats.orange, fontSize: 22 }}
        />
      ),
      title: "Active Listings",
      value:
        isLoading
          ? "—"
          : summary.activeListings != null
            ? String(summary.activeListings)
            : "—",
      // subtitle: isLoading
      //   ? undefined
      //   : summary.pendingReviewCount != null
      //     ? `${summary.pendingReviewCount} pending review`
      //     : undefined,
      // subtitleColor: (theme) => theme.palette.stats.orange,
    },
    {
      key: "total-views",
      icon: (
        <VisibilityIcon
          sx={{ color: (theme) => theme.palette.stats.purple, fontSize: 22 }}
        />
      ),
      title: "Total Views",
      value: isLoading
        ? "—"
        : summary.totalViews != null
          ? Number(summary.totalViews).toLocaleString("en-US")
          : "—",
      // subtitle: isLoading ? undefined : viewsSubtitle,
      // subtitleColor: (theme) => theme.palette.stats.purple,
    },
  ];

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: 2,
        display: "flex",
      }}
    >
      {/* inner content, fills full available space */}
      <Box
        sx={{
          width: "100%",
          minWidth: 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
            gap: 2,
          }}
        >
          {/* LEFT: icon + title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: 1,
                bgcolor: "background.headerIcon",
                border: (theme) => `1px solid ${theme.palette.border.light}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ApartmentIcon sx={{ color: "text.slate" }} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: 20,
                  lineHeight: 1.2,
                  color: "text.strong",
                }}
              >
                Asset Owner Dashboard
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: 13,
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Welcome back, {displayName} – Manage your listings
              </Typography>
            </Box>
          </Box>

          {/* RIGHT: CTA button */}
          {/* <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              px: 3.5,
              py: 1.5,
              fontSize: 14,
              fontWeight: 600,
              boxShadow: (theme) => theme.palette.brand.ctaShadow,
              background: (theme) => theme.palette.brand.ctaGradient,
              "&:hover": {
                boxShadow: (theme) => theme.palette.brand.ctaShadowHover,
                background: (theme) => theme.palette.brand.ctaGradientHover,
              },
            }}
            onClick={handleListNewAsset}
          >
            List New Asset
          </Button> */}
        </Box>

        {error ? (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.border.light}`,
              textAlign: "center",
            }}
          >
            <Typography color="error" sx={{ mb: 2 }}>
              Failed to load dashboard summary.
            </Typography>
            <Button variant="outlined" onClick={() => refetch()}>
              Retry
            </Button>
          </Paper>
        ) : null}

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {statCards.map((card) => (
            <Grid size={{ xs: 12, sm: 15, md: 3 }} key={card.key}>
              <StatCard
                icon={card.icon}
                title={card.title}
                value={card.value}
                subtitle={card.subtitle}
                subtitleColor={card.subtitleColor}
              />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8, lg: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: (theme) => `1px solid ${theme.palette.border.light}`,
                boxShadow: (theme) => theme.palette.shadow.card,
                bgcolor: "background.paper",
                height: "100%",
              }}
            >
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Quick Actions
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.muted", mb: 2.5, display: "block" }}
              >
                Get started with asset tokenization
              </Typography>

              <QuickActionItem
                icon={
                  <AddIcon
                    sx={{
                      fontSize: 18,
                      color: (theme) => theme.palette.brand.cyan,
                    }}
                  />
                }
                title="List a New Asset"
                subtitle="Submit your asset for tokenization"
                onClick={handleListNewAsset}
              />
              <QuickActionItem
                icon={
                  <ApartmentIcon
                    sx={{
                      fontSize: 18,
                      color: (theme) => theme.palette.brand.cyan,
                    }}
                  />
                }
                title="Manage Listings"
                subtitle="Track your tokenized assets"
                onClick={handleManageListings}
              />
              <QuickActionItem
                icon={
                  <DescriptionIcon
                    sx={{
                      fontSize: 18,
                      color: (theme) => theme.palette.brand.cyan,
                    }}
                  />
                }
                title="View Reports"
                subtitle="Access performance analytics"
              />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AssetOwnerDashboard;
